import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    updateProfile,
    updatePassword,
    updateEmail,
    reauthenticateWithCredential,
    EmailAuthProvider,
    sendPasswordResetEmail
} from "firebase/auth";
import {
    getFirestore,
    doc,
    setDoc,
    getDoc,
    updateDoc,
    deleteDoc,
    collection,
    query,
    where,
    getDocs,
    arrayUnion,
    serverTimestamp
} from "firebase/firestore";

// Firebase configuration - using environment variables
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
// Initialize analytics (not used directly but good for Firebase console)
getAnalytics(app);
const auth = getAuth(app);
const db = getFirestore(app);

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

// =====================================================
// AUTHENTICATION FUNCTIONS
// =====================================================

export const signUpWithEmail = async (email, password, displayName) => {
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Create user document in Firestore
        await createUserDocument(user, { displayName });

        return { success: true, user };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

export const signInWithEmail = async (email, password) => {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return { success: true, user: userCredential.user };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

export const signInWithGoogle = async () => {
    try {
        const result = await signInWithPopup(auth, googleProvider);
        const user = result.user;

        // Create user document if it doesn't exist
        await createUserDocument(user);

        return { success: true, user };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

export const logOut = async () => {
    try {
        await signOut(auth);
        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

export const resetPassword = async (email) => {
    try {
        await sendPasswordResetEmail(auth, email);
        return { success: true };
    } catch (error) {
        let message = error.message;
        if (error.code === 'auth/user-not-found') {
            message = 'No account found with this email address.';
        } else if (error.code === 'auth/invalid-email') {
            message = 'Please enter a valid email address.';
        }
        return { success: false, error: message };
    }
};

export const onAuthChange = (callback) => {
    return onAuthStateChanged(auth, callback);
};

export const updateUserProfile = async (displayName) => {
    try {
        const user = auth.currentUser;
        if (!user) {
            return { success: false, error: 'No user logged in' };
        }

        // Update Firebase Auth profile
        await updateProfile(user, { displayName });

        // Update Firestore document
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { displayName });

        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

export const updateUserEmail = async (newEmail, currentPassword) => {
    try {
        const user = auth.currentUser;
        if (!user) {
            return { success: false, error: 'No user logged in' };
        }

        // Re-authenticate user before sensitive operation
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);

        // Update email
        await updateEmail(user, newEmail);

        // Update Firestore document
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { email: newEmail });

        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

export const updateUserPassword = async (currentPassword, newPassword) => {
    try {
        const user = auth.currentUser;
        if (!user) {
            return { success: false, error: 'No user logged in' };
        }

        // Re-authenticate user before changing password
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);

        // Update password
        await updatePassword(user, newPassword);

        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// =====================================================
// FIRESTORE USER FUNCTIONS
// =====================================================

const createUserDocument = async (user, additionalData = {}) => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);
    const snapshot = await getDoc(userRef);

    // Only create if doesn't exist
    if (!snapshot.exists()) {
        const { email, displayName, photoURL } = user;
        const createdAt = serverTimestamp();

        try {
            await setDoc(userRef, {
                email,
                displayName: displayName || additionalData.displayName || 'Learner',
                photoURL: photoURL || null,
                createdAt,
                totalXP: 0,
                level: 1,
                streak: 0,
                lastLoginDate: serverTimestamp(),
                studiedTopics: [],
                completedModules: [],
                achievements: [],
                stats: {
                    totalQuestions: 0,
                    correctAnswers: 0,
                    studyTime: 0,
                    lessonsCompleted: 0
                },
                ...additionalData
            });
        } catch (error) {
            console.error('Error creating user document:', error);
        }
    } else {
        // Update last login
        await updateDoc(userRef, {
            lastLoginDate: serverTimestamp()
        });
    }
};

export const getUserData = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        const snapshot = await getDoc(userRef);

        if (snapshot.exists()) {
            return { success: true, data: snapshot.data() };
        } else {
            return { success: false, error: 'User not found' };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// Manually create user document (for fixing missing profiles)
export const initializeUserProfile = async () => {
    try {
        const user = auth.currentUser;
        if (!user) {
            return { success: false, error: 'No user logged in' };
        }

        await createUserDocument(user);
        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

export const updateUserProgress = async (userId, progressData) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            ...progressData,
            lastUpdated: serverTimestamp()
        });
        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// =====================================================
// LEARNING PATH FUNCTIONS
// =====================================================

export const saveLearningPath = async (userId, topic, learningPathData) => {
    try {
        const pathRef = doc(db, 'learningPaths', `${userId}_${topic.replace(/\s+/g, '_')}`);
        await setDoc(pathRef, {
            userId,
            topic,
            ...learningPathData,
            createdAt: serverTimestamp(),
            lastAccessed: serverTimestamp()
        });
        return { success: true };
    } catch (error) {
        console.error('Error saving learning path:', error);
        return { success: false, error: error.message };
    }
};

export const getLearningPath = async (userId, topic) => {
    try {
        const pathRef = doc(db, 'learningPaths', `${userId}_${topic.replace(/\s+/g, '_')}`);
        const snapshot = await getDoc(pathRef);

        if (snapshot.exists()) {
            // Update last accessed
            await updateDoc(pathRef, {
                lastAccessed: serverTimestamp()
            });
            return { success: true, data: snapshot.data() };
        } else {
            return { success: false, error: 'Learning path not found' };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

export const getUserLearningPaths = async (userId) => {
    try {
        const pathsRef = collection(db, 'learningPaths');
        const q = query(pathsRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(q);

        const paths = [];
        querySnapshot.forEach((doc) => {
            paths.push({ id: doc.id, ...doc.data() });
        });

        return { success: true, data: paths };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// =====================================================
// LESSON FUNCTIONS
// =====================================================

export const saveLesson = async (userId, topic, moduleId, lessonData) => {
    try {
        const docId = `${userId}_${topic.replace(/\s+/g, '_')}_${moduleId}`;
        console.log('💾 Saving lesson to Firebase:', docId);

        const lessonRef = doc(db, 'lessons', docId);
        await setDoc(lessonRef, {
            userId,
            topic,
            moduleId,
            ...lessonData,
            createdAt: serverTimestamp()
        });

        console.log('✅ Lesson saved successfully!');
        return { success: true };
    } catch (error) {
        console.error('❌ ERROR saving lesson:', error.message);
        console.error('Full error:', error);
        return { success: false, error: error.message };
    }
};

export const getLesson = async (userId, topic, moduleId) => {
    try {
        const lessonRef = doc(db, 'lessons', `${userId}_${topic.replace(/\s+/g, '_')}_${moduleId}`);
        const snapshot = await getDoc(lessonRef);

        if (snapshot.exists()) {
            return { success: true, data: snapshot.data() };
        } else {
            return { success: false, error: 'Lesson not found' };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// =====================================================
// PROGRESS TRACKING
// =====================================================

export const addXP = async (userId, xpAmount, source = 'lesson') => {
    try {
        const userRef = doc(db, 'users', userId);
        const userData = await getDoc(userRef);

        if (userData.exists()) {
            const currentXP = userData.data().totalXP || 0;
            const newXP = currentXP + xpAmount;

            // Calculate level (100 XP per level)
            const newLevel = Math.floor(newXP / 100) + 1;

            await updateDoc(userRef, {
                totalXP: newXP,
                level: newLevel,
                lastUpdated: serverTimestamp()
            });

            // Update streak when user earns XP (shows daily activity)
            await updateStreak(userId);

            return { success: true, newXP, newLevel };
        }

        return { success: false, error: 'User not found' };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

export const completeModule = async (userId, topic, moduleId) => {
    try {
        const userRef = doc(db, 'users', userId);

        // Get current user data to update stats properly
        const userSnap = await getDoc(userRef);
        const currentStats = userSnap.data()?.stats || {};
        const currentLessonsCompleted = currentStats.lessonsCompleted || 0;

        await updateDoc(userRef, {
            completedModules: arrayUnion(`${topic}_${moduleId}`),
            'stats.lessonsCompleted': currentLessonsCompleted + 1,
            lastUpdated: serverTimestamp()
        });

        console.log(`✅ Module ${topic}_${moduleId} marked as complete!`);

        // Note: XP is already added during the lesson in InteractiveLesson
        // No need to add it again here to avoid double-counting

        return { success: true };
    } catch (error) {
        console.error('Error completing module:', error);
        return { success: false, error: error.message };
    }
};

export const updateStreak = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userData = await getDoc(userRef);

        if (userData.exists()) {
            const lastActivity = userData.data().lastActivityDate?.toDate();
            const now = new Date();
            const currentStreak = userData.data().streak || 0;

            // Compare calendar dates (not milliseconds)
            // Get date strings in local timezone (YYYY-MM-DD format)
            const getDateString = (date) => {
                return date.toLocaleDateString('en-CA'); // Returns YYYY-MM-DD format
            };

            const todayStr = getDateString(now);
            const lastActivityStr = lastActivity ? getDateString(lastActivity) : null;

            // Calculate days between calendar dates
            let daysDiff = 999;
            if (lastActivityStr) {
                const today = new Date(todayStr);
                const lastDay = new Date(lastActivityStr);
                daysDiff = Math.round((today - lastDay) / (1000 * 60 * 60 * 24));
            }

            let newStreak = currentStreak;
            let shouldUpdate = false;

            if (daysDiff === 0) {
                // Same calendar day - keep streak, but ensure at least 1
                newStreak = Math.max(currentStreak, 1);
                shouldUpdate = true; // Still update the timestamp
            } else if (daysDiff === 1) {
                // Yesterday - increment streak!
                newStreak = currentStreak + 1;
                shouldUpdate = true;
            } else if (daysDiff > 1) {
                // Missed days - reset streak to 1
                newStreak = 1;
                shouldUpdate = true;
            }

            if (shouldUpdate) {
                await updateDoc(userRef, {
                    streak: newStreak,
                    lastActivityDate: serverTimestamp(),
                    lastLoginDate: serverTimestamp() // Keep for backwards compatibility
                });
            }

            return { success: true, streak: newStreak };
        }

        return { success: false, error: 'User not found' };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// =====================================================
// DELETE LEARNING PATH
// =====================================================

export const deleteLearningPath = async (userId, topic) => {
    try {
        // Delete the learning path document
        const pathRef = doc(db, 'learningPaths', `${userId}_${topic.replace(/\s+/g, '_')}`);
        await deleteDoc(pathRef);

        // Delete associated lessons
        const lessonsRef = collection(db, 'lessons');
        const q = query(lessonsRef, where('userId', '==', userId), where('topic', '==', topic));
        const querySnapshot = await getDocs(q);

        const deletePromises = [];
        querySnapshot.forEach((docSnapshot) => {
            deletePromises.push(deleteDoc(doc(db, 'lessons', docSnapshot.id)));
        });
        await Promise.all(deletePromises);

        // Delete cached world map if exists
        const worldMapRef = doc(db, 'worldMaps', `${userId}_${topic.replace(/\s+/g, '_')}`);
        await deleteDoc(worldMapRef).catch(() => {}); // Ignore if doesn't exist

        // Remove topic from user's studiedTopics
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
            const studiedTopics = userSnap.data().studiedTopics || [];
            const updatedTopics = studiedTopics.filter(t => t !== topic);
            await updateDoc(userRef, { studiedTopics: updatedTopics });
        }

        console.log(`✅ Learning path "${topic}" deleted successfully`);
        return { success: true };
    } catch (error) {
        console.error('Error deleting learning path:', error);
        return { success: false, error: error.message };
    }
};

// =====================================================
// WORLD MAP CACHING
// =====================================================

export const saveWorldMap = async (userId, topic, worldMapData, completedModulesHash) => {
    try {
        const docId = `${userId}_${topic.replace(/\s+/g, '_')}`;
        const worldMapRef = doc(db, 'worldMaps', docId);
        await setDoc(worldMapRef, {
            ...worldMapData,
            userId,
            topic,
            completedModulesHash,
            updatedAt: serverTimestamp()
        });
        console.log('✅ World map cached successfully');
        return { success: true };
    } catch (error) {
        console.error('Error caching world map:', error);
        return { success: false, error: error.message };
    }
};

export const getCachedWorldMap = async (userId, topic) => {
    try {
        const docId = `${userId}_${topic.replace(/\s+/g, '_')}`;
        const worldMapRef = doc(db, 'worldMaps', docId);
        const snapshot = await getDoc(worldMapRef);

        if (snapshot.exists()) {
            return { success: true, data: snapshot.data() };
        } else {
            return { success: false, error: 'World map not found' };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// =====================================================
// GAME COMPLETION TRACKING
// =====================================================

export const completeGame = async (userId, topic, nodeId, results) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
            return { success: false, error: 'User not found' };
        }

        const userData = userSnap.data();
        const completedGames = userData.completedGames || [];
        const gameKey = `${topic}_${nodeId}`;

        // Only add if not already completed (or update if better score)
        const existingGame = completedGames.find(g => g.key === gameKey);

        const gameData = {
            key: gameKey,
            topic,
            nodeId,
            score: results.score,
            stars: results.stars,
            xpEarned: results.xp,
            completedAt: new Date().toISOString()
        };

        let updatedGames;
        if (existingGame) {
            // Update if new score is better
            if (results.stars > existingGame.stars) {
                updatedGames = completedGames.map(g =>
                    g.key === gameKey ? gameData : g
                );
            } else {
                updatedGames = completedGames;
            }
        } else {
            updatedGames = [...completedGames, gameData];
        }

        await updateDoc(userRef, {
            completedGames: updatedGames,
            lastUpdated: serverTimestamp()
        });

        // Invalidate cached world map so it regenerates with new completion data
        const worldMapRef = doc(db, 'worldMaps', `${userId}_${topic.replace(/\s+/g, '_')}`);
        await deleteDoc(worldMapRef).catch(() => {}); // Ignore if doesn't exist

        console.log(`✅ Game ${gameKey} completed!`);
        return { success: true };
    } catch (error) {
        console.error('Error completing game:', error);
        return { success: false, error: error.message };
    }
};

export const getCompletedGames = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
            return { success: true, data: userSnap.data().completedGames || [] };
        }
        return { success: false, error: 'User not found' };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// =====================================================
// GAME CONTENT CACHING
// =====================================================

// Generate a unique key for game content
const generateGameCacheKey = (topic, templateId, moduleTitle, difficulty) => {
    const cleanTopic = topic.replace(/\s+/g, '_').toLowerCase();
    const cleanModule = (moduleTitle || 'default').replace(/\s+/g, '_').toLowerCase();
    return `game_${cleanTopic}_${templateId}_${cleanModule}_${difficulty}`;
};

// Save game content to Firestore cache
export const saveGameContent = async (userId, topic, templateId, moduleTitle, difficulty, gameContent) => {
    try {
        const cacheKey = generateGameCacheKey(topic, templateId, moduleTitle, difficulty);
        const cacheRef = doc(db, 'gameContentCache', cacheKey);

        await setDoc(cacheRef, {
            topic,
            templateId,
            moduleTitle: moduleTitle || '',
            difficulty,
            gameContent,
            createdAt: serverTimestamp(),
            lastUsedAt: serverTimestamp(),
            usageCount: 1
        });

        console.log(`✅ Game content cached: ${cacheKey}`);
        return { success: true };
    } catch (error) {
        console.error('Error caching game content:', error);
        return { success: false, error: error.message };
    }
};

// Get cached game content from Firestore
export const getGameContent = async (userId, topic, templateId, moduleTitle, difficulty) => {
    try {
        const cacheKey = generateGameCacheKey(topic, templateId, moduleTitle, difficulty);
        const cacheRef = doc(db, 'gameContentCache', cacheKey);
        const cacheSnap = await getDoc(cacheRef);

        if (cacheSnap.exists()) {
            const data = cacheSnap.data();

            // Update last used timestamp and usage count
            await updateDoc(cacheRef, {
                lastUsedAt: serverTimestamp(),
                usageCount: (data.usageCount || 0) + 1
            }).catch(() => {}); // Don't fail if update fails

            console.log(`✅ Game content cache HIT: ${cacheKey}`);
            return { success: true, data: data.gameContent, cached: true };
        }

        console.log(`⚪ Game content cache MISS: ${cacheKey}`);
        return { success: false, cached: false };
    } catch (error) {
        console.error('Error getting cached game content:', error);
        return { success: false, error: error.message, cached: false };
    }
};

export { auth, db };
